import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  HStack,
  Icon,
  Box,
} from "@chakra-ui/react";
import { MdWarning, MdDelete } from "react-icons/md";

const ConfirmationDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirm Action",
  message = "Are you sure you want to proceed?",
  confirmText = "Confirm",
  cancelText = "Cancel",
  confirmColorScheme = "red",
  isLoading = false,
  loadingText = "Processing...",
  type = "warning", // 'warning', 'danger', 'info'
  itemName = "", // e.g., "User", "Task", "Batch"
  itemIdentifier = "", // e.g., username, task ID, etc.
}) => {
  const getIcon = () => {
    switch (type) {
      case "danger":
        return MdDelete;
      case "warning":
      default:
        return MdWarning;
    }
  };

  const getIconColor = () => {
    switch (type) {
      case "danger":
        return "red.400";
      case "warning":
      default:
        return "orange.400";
    }
  };

  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay
        bg="blackAlpha.800"
        backdropFilter="blur(10px)"
        WebkitBackdropFilter="blur(10px)"
      />
      <ModalContent bg="#1C1C1C" borderColor="#3A3A3A" borderWidth="1px">
        <ModalHeader
          color="white"
          borderBottom="1px"
          borderBottomColor="#3A3A3A"
          pb="4"
        >
          <HStack spacing="3">
            <Box
              p="2"
              borderRadius="full"
              bg={getIconColor()}
              color="white"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={getIcon()} boxSize="5" />
            </Box>
            <Text fontSize="lg" fontWeight="semibold">
              {title}
            </Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton color="gray.400" />

        <ModalBody py="6">
          <VStack spacing="4" align="stretch">
            <Text color="gray.300" fontSize="md" lineHeight="1.6">
              {message}
            </Text>

            {itemName && itemIdentifier && (
              <Box
                bg="#121212"
                p="4"
                borderRadius="md"
                borderWidth="1px"
                borderColor="#3A3A3A"
              >
                <HStack justify="space-between">
                  <Text color="gray.400" fontSize="sm">
                    {itemName}:
                  </Text>
                  <Text color="white" fontSize="sm" fontWeight="medium">
                    {itemIdentifier}
                  </Text>
                </HStack>
              </Box>
            )}

            <Box
              bg="red.900"
              p="3"
              borderRadius="md"
              borderWidth="1px"
              borderColor="red.700"
            >
              <Text color="red.200" fontSize="sm" textAlign="center">
                ⚠️ This action cannot be undone
              </Text>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter borderTop="1px" borderTopColor="#3A3A3A" pt="4">
          <Button
            variant="outline"
            mr={3}
            onClick={onClose}
            borderColor="#3A3A3A"
            color="gray.300"
            _hover={{ bg: "gray.700" }}
            isDisabled={isLoading}
          >
            {cancelText}
          </Button>
          <Button
            colorScheme={confirmColorScheme}
            onClick={handleConfirm}
            isLoading={isLoading}
            loadingText={loadingText}
            _hover={{
              bg:
                confirmColorScheme === "red"
                  ? "red.600"
                  : `${confirmColorScheme}.600`,
            }}
          >
            {confirmText}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ConfirmationDialog;
